<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import ClientsDetailsLayout from 'dashboard/components-next/Clients/ClientsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ClientDetails from 'dashboard/components-next/Clients/Pages/ClientDetails.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ClientNotes from 'dashboard/components-next/Clients/ClientsSidebar/ClientNotes.vue';
import ClientHistory from 'dashboard/components-next/Clients/ClientsSidebar/ClientHistory.vue';
import ClientMerge from 'dashboard/components-next/Clients/ClientsSidebar/ClientMerge.vue';
import ClientCustomAttributes from 'dashboard/components-next/Clients/ClientsSidebar/ClientCustomAttributes.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const client = useMapGetter('clients/getClientById');
const uiFlags = useMapGetter('clients/getUIFlags');

const activeTab = ref('attributes');
const clientMergeRef = ref(null);

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingClient = computed(() => uiFlags.value.isMerging);
const isUpdatingClient = computed(() => uiFlags.value.isUpdating);

const selectedClient = computed(() => client.value(route.params.clientId));

const showSpinner = computed(
  () => isFetchingItem.value || isMergingClient.value
);

const { t } = useI18n();

const CLIENT_TABS_OPTIONS = [
  { key: 'ATTRIBUTES', value: 'attributes' },
  { key: 'HISTORY', value: 'history' },
  { key: 'NOTES', value: 'notes' },
  { key: 'MERGE', value: 'merge' },
];

const tabs = computed(() => {
  return CLIENT_TABS_OPTIONS.map(tab => ({
    label: t(`CLIENTS_LAYOUT.SIDEBAR.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return CLIENT_TABS_OPTIONS.findIndex(v => v.value === activeTab.value);
});

const goToClientsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/clients?page=1`);
  }
};

const fetchActiveClient = async () => {
  if (route.params.clientId) {
    await store.dispatch('clients/show', { id: route.params.clientId });
    await store.dispatch('clients/fetchClientableInbox', route.params.clientId);
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const fetchClientNotes = () => {
  const { clientId } = route.params;
  if (clientId) store.dispatch('clientNotes/get', { clientId });
};

const fetchClientConversations = () => {
  const { clientId } = route.params;
  if (clientId) store.dispatch('clientConversations/get', clientId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const toggleClientBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CLIENTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CLIENTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CLIENTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CLIENTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`clients/update`, {
      ...selectedClient.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(() => {
  fetchActiveClient();
  fetchClientNotes();
  fetchClientConversations();
  fetchAttributes();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <ClientsDetailsLayout
      :button-label="$t('CLIENTS_LAYOUT.HEADER.SEND_MESSAGE')"
      :selected-client="selectedClient"
      is-detail-view
      :show-pagination-footer="false"
      :is-updating="isUpdatingClient"
      @go-to-clients-list="goToClientsList"
      @toggle-block="toggleClientBlock"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <ClientDetails
        v-else-if="selectedClient"
        :selected-client="selectedClient"
        @go-to-clients-list="goToClientsList"
      />
      <template #sidebar>
        <div class="px-6">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            class="w-full [&>button]:w-full bg-n-alpha-black2"
            @tab-changed="handleTabChange"
          />
        </div>
        <div
          v-if="isFetchingItem"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <template v-else>
          <ClientCustomAttributes
            v-if="activeTab === 'attributes'"
            :selected-client="selectedClient"
          />
          <ClientNotes v-if="activeTab === 'notes'" />
          <ClientHistory v-if="activeTab === 'history'" />
          <ClientMerge
            v-if="activeTab === 'merge'"
            ref="clientMergeRef"
            :selected-client="selectedClient"
            @go-to-clients-list="goToClientsList"
            @reset-tab="handleTabChange(CLIENT_TABS_OPTIONS[0])"
          />
        </template>
      </template>
    </ClientsDetailsLayout>
  </div>
</template>
